<template>
  <m-pageframe
    :title="title"
    :title_jump_to_organization_id="organization_id"
  >
    <v-container
      fluid
      class="py-6 px-md-12"
    >
      <div v-if="profile != null">
        <v-row class="mt-0 mb-12">
          <v-col
            class="pt-0"
            cols="12"
            lg="8"
          >
            <v-row
              align="end"
              justify="center"
              justify-lg="space-between"
              class="mb-1"
            >
              <v-col
                cols="12"
                md="8"
              >
                <h2>Welcome to RedChirp!</h2>
                <h3
                  v-if="!all_steps_complete"
                  class="font-weight-regular text-body-1"
                >
                  We're excited to have you on board. Next up - please work through the onboarding steps below.
                </h3>

                <h3
                  v-else
                  class="font-weight-regular"
                >
                  🎉 Nice work! You've completed all of the onboarding steps.
                </h3>
              </v-col>

              <v-col cols="auto">
                <strong
                  class="mb-1 d-block"
                  :class="num_steps_complete > 0 ? 'success--text' : ''"
                >
                  {{ num_steps_complete }}/{{ num_steps_total }} steps complete
                </strong>

                <v-progress-linear
                  :value="progress_value"
                  height="8"
                  rounded
                  :color="num_steps_complete > 0 ? 'success' : 'primary'"
                  style="min-width: 120px; max-width: 180px;"
                  aria-label="Onboarding progress"
                />
              </v-col>
            </v-row>

            <v-expansion-panels
              class="mt-4"
              v-model="open_panel"
            >
              <v-expansion-panel
                v-for="(item, index) in optional_sections"
                :key="'opt_section_' + index"
              >
                <m-welcome-expansion-panel-header
                  :label="item.title"
                  :complete="item.is_complete"
                  :step_number="index + 1"
                />
                <v-expansion-panel-content>
                  <m-welcome-optional-section
                    :section="item"
                    v-on:reload-profile="update"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="isStepVisible('subscription_agreement')">
                <m-welcome-expansion-panel-header
                  label="Accept the RedChirp Software Subscription Agreement"
                  :complete="subscription_agreement_complete"
                  :step_number="getStepNumber('subscription_agreement')"
                />

                <v-expansion-panel-content>
                  <m-welcome-sign-subscription-agreement
                    :profile="profile"
                    v-on:reload-profile="update"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="isStepVisible('payment_method')">
                <m-welcome-expansion-panel-header
                  label="Provide a Payment Method"
                  :complete="payment_method_complete"
                  :step_number="getStepNumber('payment_method')"
                />

                <v-expansion-panel-content>
                  <m-redchirp-payment-org-controlled-billing-settings
                    :organization_id="profile.organization_account.organization_id"
                  />

                  <v-divider class="my-6" />

                  <m-redchirp-payment-methods
                    :organization_id="profile.organization_account.organization_id"
                    :show_help_when_no_payment_methods="false"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="isStepVisible('tcr_setup')">
                <m-welcome-expansion-panel-header
                  is_time_sensitive
                  label="Register Your Brand With the Carriers"
                  :complete="tcr_setup_complete"
                  :step_number="getStepNumber('tcr_setup')"
                >
                  <template
                    v-if="profile.setup_tasks.tcr_setup_data_complete"
                    #status_chip
                  >
                    <span
                      v-if="!tcr_vetting_approved"
                      class="mr-4"
                    >
                      <v-chip
                        color="success"
                        small
                        outlined
                      >
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <span
                              v-on="on"
                              v-bind="attrs"
                              style="display: inline-flex; align-items: center;"
                            >
                              <v-icon
                                left
                                size="18"
                                aria-hidden="true"
                              >
                                mdi-clock-outline
                              </v-icon>
                              <strong>Approval Pending</strong>
                            </span>
                          </template>

                          <span class="tooltip-capped">
                            Your registration has been submitted and is awaiting carrier approval. This process is
                            handled by the carriers and typically takes 10 business days.
                          </span>
                        </v-tooltip>
                      </v-chip>
                    </span>

                    <span
                      v-else
                      class="mr-4"
                    >
                      <v-chip
                        color="success"
                        small
                      >
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <span
                              v-on="on"
                              v-bind="attrs"
                              style="display: inline-flex; align-items: center;"
                            >
                              <v-icon
                                left
                                size="18"
                                aria-hidden="true"
                              >
                                mdi-check-circle
                              </v-icon>
                              <strong>Approved</strong>
                            </span>
                          </template>
                          <span class="tooltip-capped">
                            Congrats! Your registration has been approved by the carriers.
                          </span>
                        </v-tooltip>
                      </v-chip>
                    </span>
                  </template>
                </m-welcome-expansion-panel-header>

                <v-expansion-panel-content>
                  <m-welcome-tcr
                    :profile="profile"
                    v-on:reload="update"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="isStepVisible('general_setup')">
                <m-welcome-expansion-panel-header
                  label="General Information"
                  :complete="general_setup_complete"
                  :step_number="getStepNumber('general_setup')"
                />
                <v-expansion-panel-content>

                  <m-welcome-general
                    :profile="profile"
                    v-on:reload="update"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="isStepVisible('website_setup')">
                <m-welcome-expansion-panel-header
                  label="Enable Website Tools"
                  :complete="website_setup_complete"
                  :step_number="getStepNumber('website_setup')"
                />

                <v-expansion-panel-content>
                  <m-welcome-website
                    :profile="profile"
                    v-on:reload="update"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="isStepVisible('integrations_setup')">
                <m-welcome-expansion-panel-header
                  label="Integrations"
                  :complete="integrations_setup_complete"
                  :step_number="getStepNumber('integrations_setup')"
                />
                <v-expansion-panel-content>

                  <m-welcome-integrations
                    :profile="profile"
                    v-on:reload="update"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel v-if="isStepVisible('team_members')">
                <m-welcome-expansion-panel-header
                  label="Set Up Your Team Members"
                  :complete="team_members_complete"
                  :step_number="getStepNumber('team_members')"
                />
                <v-expansion-panel-content>
                  <m-welcome-setup-team
                    :profile="profile"
                    v-on:update-profile="updateProfile"
                    v-on:reload-profile="update"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <template
              v-if="$rc.$state.isAuthActorSystemAdmin() || (profile?.numbering_plan.state != 'do-not-show' && is_org_admin)"
            >
              <h4 class="mt-8 mb-4">Additional Configuration</h4>

              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon>
                        mdi-cellphone
                      </v-icon>
                      Phone Number Plan
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <m-welcome-numbering-plan
                      :profile="profile"
                      v-on:update-profile="update"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </template>
          </v-col>

          <v-col
            cols="12"
            lg="4"
          >
            <welcome-sidebar
              :profile="profile"
              :has_tcr_step="isStepVisible('tcr_setup')"
              :tcr_step_number="getStepNumber('tcr_setup')"
              :tcr_setup_complete="tcr_setup_complete"
              :tcr_vetting_approved="tcr_vetting_approved"
              :steps_complete_percentage="progress_value"
              @update="update"
            />
          </v-col>
        </v-row>

        <p v-if="$rc.$state.isAuthActorSystemAdmin()"><b>System administrators only</b></p>

        <m-welcome-sys-admins-only
          :profile="profile"
          style="margin-top: 30px"
          v-on:update-profile="updateProfile"
          v-on:reload-profile="update"
          ref="sys-admins-only"
        />
      </div>

      <m-eula-sign-dialog ref="m-eula-sign-dialog" />

      <div
        style="position: fixed; right: 15px; bottom: 30px; z-index: 10000"
        v-if="is_mobile"
      >
        <v-menu
          top
          offset-y
        >
          <template v-slot:activator="{ on }">

            <v-btn
              v-on="on"
              color="grey"
              dark
              fab
              style="margin-right: 8px;"
            >
              <v-icon>mdi-menu</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-on:click="onClickAddToHomeScreen()">
              <v-list-item-title>Add RedChirp to your home screen...</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-container>
  </m-pageframe>
</template>

<script>
import WelcomeSidebar from './welcome-sidebar.vue';

export default {
  components: {
    WelcomeSidebar,
  },

  data() {
    return {
      profile: null,
      campaign_status: null,
      open_panel: null,
      num_steps_complete: 0,
      num_steps_remaining: 0,
    }
  },

  mounted() {
    this.open_panel = this.$rc.getOpenPanel(this.$route);
    this.update();
    this.checkEULA();
  },

  methods: {
    update() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'GET: /api/v1/welcome/get-profile/:employee_id',

        params: {
          employee_id: this.$route.params.employee_id,
        },

        on_success: (data) => {
          this.updateProfile(data);

          if ( this.$route.query.origin != 'nav' )
          {
            if (data.organization_account.organization_state != 'pre-launch' && !this.$rc.$state.isAuthActorSystemAdmin()) {
              this.$router.push({
                name: 'home',
                params: {}
              });
            }
          }

          this.$nextTick(() => {
            this.calculateProgress();
          });
        },

        on_error: 'Error fetching welcome screen profile'
      }); ``
    },

    updateProfile(profile) {
      this.profile = profile;

      this.$nextTick(() => {
        this.calculateProgress();
      });
    },

    getCampaignStatus() {
      if (!this.profile || !this.organization_id) return;

      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: /api/v1/tcr/campaigns',

        params: {
          organization_id: this.organization_id,
        },

        on_success: (data) => {
          this.campaign_status = data.results?.[0] || null;
        },

        on_error: 'Unable to find campaign',
      });
    },

    checkEULA() {
      this.$rc.checkEULA(
        (eula_check) => {

          if (eula_check.signature_current == true)
            return;

          this.$refs['m-eula-sign-dialog'].showDialog(this.$rc.$state.employee_id);
        }
      );
    },

    onClickAddToHomeScreen() {
      this.$router.push({
        'name': 'ios-to-instructions'
      });
    },

    calculateProgress() {
      let complete = 0;
      let remaining = 0;

      if (this.optional_sections && this.optional_sections.length > 0) {
        this.optional_sections.forEach(section => {
          if (section.is_complete) {
            complete++;
          } else {
            remaining++;
          }
        });
      }

      const steps = [
        { visible: this.isStepVisible('subscription_agreement'), complete: this.subscription_agreement_complete },
        { visible: this.isStepVisible('payment_method'), complete: this.payment_method_complete },
        { visible: this.isStepVisible('tcr_setup'), complete: this.tcr_setup_complete },
        { visible: this.isStepVisible('general_setup'), complete: this.general_setup_complete },
        { visible: this.isStepVisible('website_setup'), complete: this.website_setup_complete },
        { visible: this.isStepVisible('integrations_setup'), complete: this.integrations_setup_complete },
        { visible: this.isStepVisible('team_members'), complete: this.team_members_complete },
      ];

      steps.forEach(step => {
        if (step.visible) {
          if (step.complete) {
            complete++;
          } else {
            remaining++;
          }
        }
      });

      this.num_steps_complete = complete;
      this.num_steps_remaining = remaining;
    },

    getStepNumber(stepType) {
      if (!this.profile || !this.is_org_admin) return 1;

      let stepNumber = 1;

      if (this.optional_sections && this.optional_sections.length > 0) {
        if (stepType.startsWith('optional_')) {
          const optionalIndex = parseInt(stepType.replace('optional_', ''));
          return optionalIndex + 1;
        }
        stepNumber += this.optional_sections.length;
      }

      const stepOrder = [
        'subscription_agreement',
        'payment_method',
        'tcr_setup',
        'general_setup',
        'website_setup',
        'integrations_setup',
        'team_members'
      ];

      for (const step of stepOrder) {
        const isVisible = this.isStepVisible(step);

        if (step === stepType) {
          return isVisible ? stepNumber : stepNumber;
        }

        if (isVisible) {
          stepNumber++;
        }
      }

      return stepNumber;
    },

    isStepVisible(stepType) {
      if (!this.profile || !this.is_org_admin) return false;

      switch (stepType) {
        case 'subscription_agreement':
          return !this.profile.setup_tasks.sign_subscription_agreement_outside_of_redchirp;
        case 'payment_method':
          return !this.profile.setup_tasks.payment_outside_of_redchirp;
        case 'tcr_setup':
          return !this.profile.setup_tasks.tcr_setup_outside_redchirp;
        case 'general_setup':
          return !this.profile.setup_tasks.general_setup_outside_redchirp;
        case 'website_setup':
          return !this.profile.setup_tasks.website_setup_outside_redchirp;
        case 'integrations_setup':
          return !this.profile.setup_tasks.integrations_setup_outside_redchirp;
        case 'team_members':
          return this.is_org_admin;
        default:
          return false;
      }
    },
  },

  computed: {
    num_steps_total() {
      return this.num_steps_complete + this.num_steps_remaining;
    },

    all_steps_complete() {
      return this.num_steps_remaining == 0;
    },

    is_org_admin() {
      if (this.profile == null) return false;
      if (this.profile.employee_account.is_organization_admin != true) return false;

      return true;
    },

    title() {
      if (this.profile == null)
        return 'Welcome to RedChirp';

      return 'Welcome to RedChirp: ' + this.profile.organization_account.label;
    },

    organization_id() {
      return this.$rc.getDeep(this.profile, 'organization_account/organization_id');
    },

    is_mobile() {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm';
    },

    optional_sections() {
      if (this.profile == null) return [];
      if (this.profile.employee_account.is_organization_admin != true) return [];

      return this.profile.optional_sections;
    },

    subscription_agreement_complete() {
      return this.profile?.subscription_signature != null;
    },

    payment_method_complete() {
      return this.profile?.setup_tasks?.setup_payment_method_complete;
    },

    tcr_setup_complete() {
      return this.profile?.setup_tasks?.tcr_setup_data_complete;
    },

    general_setup_complete() {
      return this.profile?.setup_tasks?.general_setup_data_complete;
    },

    website_setup_complete() {
      return this.profile?.setup_tasks?.website_setup_data_complete;
    },

    integrations_setup_complete() {
      return this.profile?.setup_tasks?.integrations_setup_data_complete;
    },

    numbering_plan_complete() {
      return this.profile?.numbering_plan?.state == 'complete';
    },

    team_members_complete() {
      return this.profile?.setup_tasks?.setup_team_members_complete;
    },

    progress_value() {
      if (!this.num_steps_total || !this.num_steps_complete) return 0;
      return this.num_steps_total > 0 ? (this.num_steps_complete / this.num_steps_total) * 100 : 0
    },

    tcr_vetting_status() {
      return this.campaign_status?.vetting_status || null;
    },

    tcr_vetting_approved() {
      if (!this.tcr_vetting_status) return false;
      return ['dca2-approved'].includes(this.tcr_vetting_status);
    }
  },

  watch: {
    organization_id: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.getCampaignStatus();
        } else {
          this.campaign_status = null;
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.v-expansion-panel-content__wrap) {
  padding-top: 1.25rem;
}

:deep(.v-stepper--vertical .v-stepper__step) {
  padding: 1rem 1.5rem;
}
</style>